import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { SortDirection } from '@e-origin/shared';

const c = initContract();

export const UserShape = z.any();

export const userContract = c.router({
  registerUser: {
    method: 'POST',
    path: '/user/register',
    body: z.object({
      email: z.string().email(),
      password: z.string(),
      role: z.string().optional(),
    }),
    responses: {
      201: z.object({ token: z.string() }),
      400: z.object({ message: z.string() }),
    },
  },
  signupUser: {
    method: 'POST',
    path: '/user/signup',
    body: z.object({
      firstName: z.string(),
      lastName: z.string(),
      email: z.string().email(),
      password: z.string(),
      confirmationPassword: z.string(),
    }),
    responses: {
      201: z.object({ user: UserShape, declarant: z.string() }),
      400: z.object({ message: z.string() }),
    },
  },
  activateUser: {
    method: 'POST',
    path: '/user/activate',
    body: z.object({
      email: z.string().email(),
      activationCode: z.string(),
    }),
    responses: {
      200: z.object({ token: z.string() }),
      400: z.object({ message: z.string() }),
    },
  },
  contactSalesSupport: {
    method: 'POST',
    path: '/user/contact-sales-support',
    body: c.noBody(),
    responses: {
      200: z.object({ message: z.string() }),
      400: z.object({ message: z.string() }),
    },
  },
  step2UpdateInfo: {
    method: 'POST',
    path: '/user/step2-update-info',
    body: z.object({
      declarantData: z.object({
        name: z.string(),
        identificationNumber: z.string(),
        address: z.object({
          street: z.string(),
          number: z.string(),
          postcode: z.string(),
          city: z.string(),
          country: z.string(),
        }),
      }),
    }),
    responses: {
      200: z.object({}),
      400: z.object({ message: z.string() }),
    },
  },
  loginUser: {
    method: 'POST',
    path: '/user/login',
    body: z.object({
      email: z.string().email(),
      password: z.string(),
      visitorId: z.string().optional(),
      recaptchaToken: z.string().optional(),
    }),
    responses: {
      200: z.object({ token: z.string() }),
      401: z.object({ message: z.string() }),
    },
  },
  adminUpdateUser: {
    method: 'PATCH',
    path: '/user/',
    body: z.object({
      email: z.string().email(),
      password: z.string().optional(),
      firstName: z.string().optional(),
      lastName: z.string().optional(),
      isSupervisor: z.boolean().optional(),
    }),
    responses: {
      201: z.object({ user: UserShape }),
      404: z.object({ message: z.string() }),
    },
  },
  deleteUser: {
    method: 'DELETE',
    path: '/user/:email',
    body: c.noBody(),
    pathParams: z.object({ email: z.string().email() }),
    responses: {
      204: z.object({}),
    },
  },
  enableUser: {
    method: 'PATCH',
    path: '/user/:email/enable',
    body: c.noBody(),
    pathParams: z.object({ email: z.string().email() }),
    responses: {
      201: z.object({ user: UserShape }),
      404: z.object({ message: z.string() }),
    },
  },
  retrieveDeclarantUsers: {
    method: 'POST',
    path: '/user/',
    body: z.object({
      criteria: z
        .object({
          filters: z.object({ searchQuery: z.string().optional(), filterByDeclarants: z.array(z.string()).optional() }).optional(),
          pagination: z.object({ page: z.number().optional(), size: z.number().optional() }).optional(),
          sorting: z.object({ field: z.string().optional(), direction: z.enum([SortDirection.ASC, SortDirection.DESC]).optional() }).optional(),
        })
        .optional(),
    }),
    responses: {
      200: z.object({
        data: z.object({
          users: z.array(UserShape),
          totalItems: z.number(),
        }),
      }),
      500: z.object({ message: z.string() }),
    },
  },
  getMe: {
    method: 'GET',
    path: '/user/me',
    responses: {
      200: z.object({
        data: z.object({
          user: UserShape,
          declarant: z.any(),
        }),
      }),
      404: z.object({ message: z.string() }),
    },
  },
  createUser: {
    method: 'POST',
    path: '/user/create-user',
    body: z.object({
      email: z.string().email(),
      firstName: z.string(),
      lastName: z.string(),
      password: z.string(),
    }),
    responses: {
      201: z.object({
        data: UserShape,
        message: z.string(),
      }),
    },
  },
  editUser: {
    method: 'PUT',
    path: '/user/edit-user/:userId',
    pathParams: z.object({ userId: z.string() }),
    body: z.object({
      firstName: z.string(),
      lastName: z.string(),
      visible: z.boolean().optional(),
      deleted: z.boolean().optional(),
    }),
    responses: {
      200: z.object({
        data: UserShape,
        message: z.string(),
      }),
    },
  },
  updateRiskAnalysisParameters: {
    method: 'PUT',
    path: '/user/update-risk-analysis-parameters',
    body: z.object({
      vatValidationEnabled: z.boolean(),
      eoriValidationEnabled: z.boolean(),
      customerEvaluationMethods: z.array(z.any()),
    }),
    responses: {
      200: z.object({ message: z.string() }),
      404: z.object({ message: z.string() }),
    },
  },
  getRiskAnalysisParameters: {
    method: 'GET',
    path: '/user/:userId/risk-analysis-parameters',
    pathParams: z.object({ userId: z.string() }),
    responses: {
      200: z.object({ data: UserShape, message: z.string() }),
      404: z.object({ message: z.string() }),
    },
  },
  refreshToken: {
    method: 'POST',
    path: '/user/refresh-token',
    body: z.object({ existingToken: z.string().optional(), visitorId: z.string().optional() }),
    responses: {
      200: z.object({ refreshedToken: z.string() }),
      400: z.object({ message: z.string() }),
    },
  },
  forgotPassword: {
    method: 'POST',
    path: '/user/forgot-password',
    body: z.object({ email: z.string().email() }),
    responses: {
      200: z.object({}),
      400: z.object({ message: z.string() }),
    },
  },
  checkResetPassword: {
    method: 'POST',
    path: '/user/check-reset-password',
    body: z.object({ resetToken: z.string() }),
    responses: {
      200: z.object({ valid: z.boolean() }),
      400: z.object({ message: z.string() }),
    },
  },
  resetPassword: {
    method: 'POST',
    path: '/user/reset-password',
    body: z.object({
      password: z.string(),
      passwordConfirmation: z.string(),
      resetToken: z.string(),
    }),
    responses: {
      200: z.object({}),
      400: z.object({ message: z.string() }),
    },
  },
  acceptVatReportDisclaimer: {
    method: 'POST',
    path: '/user/accept-vat-report-disclaimer',
    body: c.noBody(),
    responses: {
      200: z.object({ message: z.string() }),
      404: z.object({ message: z.string() }),
    },
  },
});
